/**
 * EditForm Component
 *
 * Form for editing existing bookings. Supports:
 * - Changing project (hard bookings)
 * - Changing customer (soft bookings)
 * - Converting to absence
 * - Modifying hours
 * - Bulk editing multiple bookings
 *
 * Flow:
 * 1. If multiple projects/customers in selection, show source picker
 * 2. Edit form with target project/customer and hours
 * 3. Conflict check and resolution
 * 4. Save changes
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

// Hooks
import { useProjects } from "@/hooks/data/useProjects";
import { useCustomers } from "@/hooks/data/useCustomers";
import { useBookingMutations } from "@/hooks/booking/useBookingMutations";
import { useConflictResolution } from "@/hooks/booking/useConflictResolution";
import { useUser } from "@/hooks/auth/useUser";

// Types
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { BookingType } from "@/services/types";
import type {
  EditFormProps,
  EditFormState,
  SourceSelectionItem,
} from "./types";
import { isCustomerTarget, isBookingTarget } from "../shared";

// Utils - select option converters
import {
  toProjectOptions,
  toCustomerOptions,
  selectedProjectOption as toSelectedProjectOption,
  selectedCustomerOption as toSelectedCustomerOption,
} from "@/utils/selectOptions";

// Utils - form logic (pure functions)
import {
  collectBookings,
  groupBookingsByProject,
  groupBookingsByCustomer,
  determineBookingType,
  determineInitialStep,
  findInitialSourceProject,
  findInitialSourceCustomer,
  hasValidTarget as checkHasValidTarget,
  checkOverbookedDays,
  buildPostBookings,
  formatSourceLabel,
  getInitialHoursForSource,
  ABSENCE_PROJECT,
  countWeekendBookings,
  countHolidayBookings,
  countWarningRedBookings,
  filterRedBookings,
} from "./utils";

// Constants
import {
  DEFAULT_HOURS,
  MAX_HOURS_OVERTIME,
  MAX_HOURS_DAY,
} from "@/constants/booking";

// Shared Components
import {
  FormContainer,
  FormHeader,
  FormActions,
  SavingStep,
  HoursInput,
  ProjectSelector,
  CustomerSelector,
  AbsenceCheckbox,
} from "../shared";

// UI Components
import { Modal } from "@/components/ui/feedback/Modal";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Icon } from "@/components/ui/display/Icon";

// Step Components
import { ConflictStep, SourceSelectStep } from "./steps";

// Styles
import formStyles from "../shared/formUtilities.module.css";
import { clampHours } from "../AddForm/utils";

export const EditForm: React.FC<EditFormProps> = ({
  targetEntity,
  selectedCards,
  bookingType = "hard",
  defaultHours = DEFAULT_HOURS,
  absenceDisabled = false,
  onDone,
  onError,
  className,
  "data-testid": testId,
}) => {
  // ============================================================
  // DATA HOOKS
  // ============================================================
  const { activeProjects, isLoading: projectsLoading } = useProjects();
  const { activeCustomers, isLoading: customersLoading } = useCustomers();
  const { updateBookings } = useBookingMutations();
  const conflictResolution = useConflictResolution(defaultHours);
  const { organizationSettings } = useUser();

  // ============================================================
  // DERIVED DATA (from selection)
  // ============================================================
  const allBookings = useMemo(
    () => collectBookings(targetEntity, selectedCards, isBookingTarget),
    [targetEntity, selectedCards],
  );

  const effectiveBookingType = useMemo(
    () => determineBookingType(allBookings, bookingType),
    [allBookings, bookingType],
  );

  const projectsInSelection = useMemo(
    () => groupBookingsByProject(allBookings),
    [allBookings],
  );

  const customersInSelection = useMemo(
    () => groupBookingsByCustomer(allBookings),
    [allBookings],
  );

  // ============================================================
  // INITIAL STATE
  // ============================================================
  const initialStep = useMemo(
    () =>
      determineInitialStep(
        effectiveBookingType,
        projectsInSelection,
        customersInSelection,
      ),
    [effectiveBookingType, projectsInSelection, customersInSelection],
  );

  const initialSourceProject = useMemo(
    () => findInitialSourceProject(projectsInSelection, activeProjects),
    [projectsInSelection, activeProjects],
  );

  const initialSourceCustomer = useMemo(
    () =>
      findInitialSourceCustomer(
        customersInSelection,
        activeCustomers,
        targetEntity,
        allBookings,
        isCustomerTarget,
      ),
    [customersInSelection, activeCustomers, targetEntity, allBookings],
  );

  const [showWeekendHolidayDialog, setShowWeekendHolidayDialog] =
    useState(false);

  const [state, setState] = useState<EditFormState>(() => {
    const sourceId =
      effectiveBookingType === "hard"
        ? (initialSourceProject?.id ?? null)
        : (initialSourceCustomer?.id ?? null);
    const hours = getInitialHoursForSource(
      sourceId,
      allBookings,
      effectiveBookingType,
      defaultHours,
    );

    return {
      step: initialStep,
      sourceProject: initialSourceProject,
      targetProject: initialSourceProject,
      sourceCustomer: initialSourceCustomer,
      targetCustomer: initialSourceCustomer,
      hours,
      isAbsence:
        initialSourceProject?.name === "Absence" ||
        projectsInSelection[0]?.isAbsence === true,
      allowOvertime: hours > defaultHours,
      preAbsenceProject: null, // Bug #2 fix: Store project before toggling absence
    };
  });

  // ============================================================
  // COMPUTED VALUES
  // ============================================================
  const isTargetCustomer = isCustomerTarget(targetEntity);
  const isCustomerOnlySoftBooking =
    effectiveBookingType === "soft" && isTargetCustomer && !state.isAbsence;
  const maxHours = useMemo<number | undefined>(() => {
    if (isCustomerOnlySoftBooking) return undefined;
    return state.allowOvertime ? MAX_HOURS_OVERTIME : defaultHours;
  }, [isCustomerOnlySoftBooking, state.allowOvertime, defaultHours]);

  const sourceBookings = useMemo(() => {
    if (effectiveBookingType === "hard" && state.sourceProject) {
      // Absence source (id: -1) → filter for absence bookings
      if (state.sourceProject.id === -1) {
        return allBookings.filter((b) => b.b_type === BookingType.ABSENCE);
      }
      return allBookings.filter(
        (b) => b.project?.id === state.sourceProject?.id,
      );
    }
    if (effectiveBookingType === "soft" && state.sourceCustomer) {
      return allBookings.filter(
        (b) => b.customer?.id === state.sourceCustomer?.id,
      );
    }
    return allBookings;
  }, [
    allBookings,
    effectiveBookingType,
    state.sourceProject,
    state.sourceCustomer,
  ]);

  const weekendDays = useMemo(
    () => countWeekendBookings(sourceBookings),
    [sourceBookings],
  );

  const holidayDays = useMemo(
    () => countHolidayBookings(sourceBookings),
    [sourceBookings],
  );

  const canSkipWeekends =
    weekendDays > 0 && !organizationSettings.include_weekends;

  const canSkipHolidays =
    holidayDays > 0 && !organizationSettings.include_holidays;

  const canSkipRedDays = canSkipWeekends || canSkipHolidays;

  const warningWeekendDays = canSkipWeekends ? weekendDays : 0;
  const warningHolidayDays = canSkipHolidays ? holidayDays : 0;

  const warningRedDayCount = useMemo(
    () =>
      countWarningRedBookings(sourceBookings, {
        skipWeekends: canSkipWeekends,
        skipHolidays: canSkipHolidays,
      }),
    [sourceBookings, canSkipWeekends, canSkipHolidays],
  );

  const skipButtonLabel = useMemo(() => {
    if (canSkipWeekends && canSkipHolidays) {
      return "Hopp over helger og helligdager";
    }
    if (canSkipWeekends) {
      return "Hopp over helger";
    }
    if (canSkipHolidays) {
      return "Hopp over helligdager";
    }
    return "";
  }, [canSkipWeekends, canSkipHolidays]);

  const warningRedDayText = useMemo(() => {
    const hasWeekends = warningWeekendDays > 0;
    const hasHolidays = warningHolidayDays > 0;

    if (hasWeekends && hasHolidays) {
      return `${warningRedDayCount} booking${warningRedDayCount === 1 ? "" : "er"} på helgedager eller helligdager`;
    }
    if (hasWeekends) {
      return `${warningRedDayCount} booking${warningRedDayCount === 1 ? "" : "er"} på helgedag${warningRedDayCount === 1 ? "" : "er"}`;
    }
    if (hasHolidays) {
      return `${warningRedDayCount} booking${warningRedDayCount === 1 ? "" : "er"} på helligdag${warningRedDayCount === 1 ? "" : "er"}`;
    }
    return `${warningRedDayCount} booking${warningRedDayCount === 1 ? "" : "er"}`;
  }, [warningWeekendDays, warningHolidayDays, warningRedDayCount]);

  const warningDialogTitle = useMemo(() => {
    const hasWeekends = warningWeekendDays > 0;
    const hasHolidays = warningHolidayDays > 0;

    if (hasWeekends && hasHolidays) {
      return "Redigering inkluderer helger og helligdager";
    }
    if (hasWeekends) {
      return "Redigering inkluderer helger";
    }
    if (hasHolidays) {
      return "Redigering inkluderer helligdager";
    }
    return "Redigering inkluderer ikke-arbeidsdager";
  }, [warningWeekendDays, warningHolidayDays]);

  const hasValidTarget = useMemo(
    () =>
      checkHasValidTarget(
        state,
        effectiveBookingType,
        targetEntity,
        isCustomerTarget,
      ),
    [state, effectiveBookingType, targetEntity],
  );

  const sourceLabel = formatSourceLabel(
    effectiveBookingType,
    state.sourceProject,
    state.sourceCustomer,
    allBookings,
  );
  const showBackButton =
    effectiveBookingType === "hard"
      ? projectsInSelection.length > 1
      : customersInSelection.length > 1;

  // ============================================================
  // SELECT OPTIONS
  // ============================================================
  const projectOptions = useMemo(
    () => toProjectOptions(activeProjects),
    [activeProjects],
  );
  const customerOptions = useMemo(
    () => toCustomerOptions(activeCustomers),
    [activeCustomers],
  );

  const selectedProjectOption = useMemo(() => {
    if (!state.targetProject || state.targetProject.name === "Absence")
      return [];
    return toSelectedProjectOption(state.targetProject);
  }, [state.targetProject]);

  const selectedCustomerOption = useMemo(
    () => toSelectedCustomerOption(state.targetCustomer),
    [state.targetCustomer],
  );

  // ============================================================
  // HANDLERS - Source Selection
  // ============================================================
  const handleSelectSource = useCallback(
    (item: SourceSelectionItem) => {
      const hours = getInitialHoursForSource(
        item.id,
        allBookings,
        effectiveBookingType,
        defaultHours,
      );

      if (effectiveBookingType === "hard") {
        // For absence items (id: -1), use ABSENCE_PROJECT instead of searching activeProjects
        const project = item.isAbsence
          ? ABSENCE_PROJECT
          : (activeProjects.find((p) => p.id === item.id) ?? null);
        setState((prev) => ({
          ...prev,
          sourceProject: project,
          targetProject: project,
          hours,
          isAbsence: item.isAbsence ?? false,
          allowOvertime: hours > defaultHours,
        }));
      } else {
        const customer = activeCustomers.find((c) => c.id === item.id) ?? null;
        setState((prev) => ({
          ...prev,
          sourceCustomer: customer,
          targetCustomer: customer,
          hours,
          allowOvertime:
            effectiveBookingType === "soft" && isCustomerTarget(targetEntity)
              ? false
              : hours > defaultHours,
        }));
      }
    },
    [
      effectiveBookingType,
      activeProjects,
      activeCustomers,
      allBookings,
      defaultHours,
    ],
  );

  const handleContinueFromSelect = useCallback(() => {
    setState((prev) => ({ ...prev, step: "edit" }));
  }, []);

  const handleBackToSelect = useCallback(() => {
    setState((prev) => ({ ...prev, step: "selectSource" }));
  }, []);

  // ============================================================
  // HANDLERS - Form Fields
  // ============================================================
  const handleProjectChange = useCallback(
    (selected: SelectOption<number>[]) => {
      const project =
        selected.length > 0
          ? (activeProjects.find((p) => p.id === selected[0].id) ?? null)
          : null;
      setState((prev) => ({ ...prev, targetProject: project }));
    },
    [activeProjects],
  );

  const handleCustomerChange = useCallback(
    (selected: SelectOption<number>[]) => {
      const customer =
        selected.length > 0
          ? (activeCustomers.find((c) => c.id === selected[0].id) ?? null)
          : null;
      setState((prev) => ({ ...prev, targetCustomer: customer }));
    },
    [activeCustomers],
  );

  const handleHoursChange = useCallback(
    (hours: number) => {
      setState((prev) => ({
        ...prev,
        hours: clampHours(hours, maxHours),
      }));
    },
    [maxHours],
  );

  const handleHoursInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value)) {
        setState((prev) => ({
          ...prev,
          hours: clampHours(value, maxHours),
        }));
      }
    },
    [maxHours],
  );

  const handleAbsenceToggle = useCallback(() => {
    setState((prev) => {
      const togglingOn = !prev.isAbsence;
      if (togglingOn) {
        // Toggling ON absence - save current targetProject and set to ABSENCE_PROJECT
        // This preserves any changes user made to the project dropdown (Bug #2 fix)
        return {
          ...prev,
          isAbsence: true,
          targetProject: ABSENCE_PROJECT,
          preAbsenceProject: prev.targetProject, // Save project before switching to absence
          allowOvertime: false,
        };
      } else {
        // Toggling OFF absence - restore the project user had selected before toggling
        // Priority: 1) preAbsenceProject (saved project), 2) sourceProject, 3) null
        let restoredProject = null;

        if (prev.preAbsenceProject) {
          // User had selected a project before toggling absence
          restoredProject = prev.preAbsenceProject;
        } else if (prev.sourceProject) {
          // Fall back to sourceProject if valid
          restoredProject = prev.sourceProject;
        }

        return {
          ...prev,
          isAbsence: false,
          targetProject: restoredProject,
          preAbsenceProject: null, // Clear the preserved project
        };
      }
    });
  }, []);

  const handleOvertimeToggle = useCallback(() => {
    if (isCustomerOnlySoftBooking) return;

    setState((prev) => {
      const newAllowOvertime = !prev.allowOvertime;
      const newHours = newAllowOvertime
        ? prev.hours
        : Math.min(prev.hours, defaultHours);
      return { ...prev, allowOvertime: newAllowOvertime, hours: newHours };
    });
  }, [defaultHours, isCustomerOnlySoftBooking]);

  // ============================================================
  // HANDLERS - Submission
  // ============================================================
  // Helper function to proceed with submission (optionally with filtered bookings)
  const proceedWithSubmission = useCallback(
    async (bookingsToEdit = sourceBookings) => {
      // Validate overbooking against the final booking set (supports "skip weekends/holidays")
      if (
        checkOverbookedDays(
          effectiveBookingType,
          bookingsToEdit,
          selectedCards,
          state.hours,
          MAX_HOURS_DAY,
        )
      ) {
        onError?.(
          "En eller flere dager har for mange bookede timer. Maksimum er 24 timer per dag.",
        );
        return;
      }

      const postBookings = buildPostBookings(
        bookingsToEdit,
        state,
        effectiveBookingType,
      );
      if (postBookings.length === 0) {
        onError?.("Ingen bookinger å oppdatere.");
        return;
      }

      const ignoreIds = bookingsToEdit.map((b) => b.id);
      setState((prev) => ({ ...prev, step: "conflict" }));
      await conflictResolution.startResolution(postBookings, {
        ignoredBookingIds: ignoreIds,
        autoResolve: true,
      });
    },
    [
      sourceBookings,
      state,
      effectiveBookingType,
      selectedCards,
      conflictResolution,
      onError,
    ],
  );

  const handleSubmit = useCallback(async () => {
    if (warningRedDayCount > 0) {
      setShowWeekendHolidayDialog(true);
      return;
    }

    await proceedWithSubmission();
  }, [warningRedDayCount, proceedWithSubmission]);

  // ============================================================
  // HANDLERS - Weekend/Holiday Dialog
  // ============================================================
  const handleDialogCancel = useCallback(() => {
    setShowWeekendHolidayDialog(false);
  }, []);

  const handleEditAnyway = useCallback(async () => {
    setShowWeekendHolidayDialog(false);
    await proceedWithSubmission(sourceBookings);
  }, [proceedWithSubmission, sourceBookings]);

  const handleSkipRedDays = useCallback(async () => {
    setShowWeekendHolidayDialog(false);

    const filteredBookings = filterRedBookings(sourceBookings, {
      skipWeekends: canSkipWeekends,
      skipHolidays: canSkipHolidays,
    });

    if (filteredBookings.length === 0) {
      onError?.(
        "Ingen bookinger å redigere etter å ha fjernet relevante helger og/eller helligdager.",
      );
      return;
    }

    await proceedWithSubmission(filteredBookings);
  }, [
    proceedWithSubmission,
    sourceBookings,
    onError,
    canSkipWeekends,
    canSkipHolidays,
  ]);

  const handleConflictCancel = useCallback(() => {
    conflictResolution.cancelResolution();
    setState((prev) => ({ ...prev, step: "edit" }));
  }, [conflictResolution]);

  const handleConflictResolve = useCallback(async () => {
    setState((prev) => ({ ...prev, step: "saving" }));

    try {
      const postsToSave = conflictResolution.completeResolution();
      if (postsToSave.length === 0) {
        onError?.("Ingen bookinger å lagre etter konfliktløsning.");
        setState((prev) => ({ ...prev, step: "edit" }));
        return;
      }

      await updateBookings(postsToSave);
      onDone?.("Endringer lagret");
    } catch (error) {
      const message =
        error instanceof Error && error.message === "FORBIDDEN"
          ? "Ikke autorisert til å lagre endringer"
          : "Kunne ikke lagre endringer";
      onError?.(message);
      setState((prev) => ({ ...prev, step: "edit" }));
    }
  }, [conflictResolution, updateBookings, onDone, onError]);

  // Auto-advance when conflict check finds no issues.
  useEffect(() => {
    if (state.step !== "conflict") {
      return;
    }
    if (conflictResolution.isLoading || conflictResolution.error) {
      return;
    }
    if (!conflictResolution.hasConflicts) {
      void handleConflictResolve();
    }
  }, [
    state.step,
    conflictResolution.isLoading,
    conflictResolution.error,
    conflictResolution.hasConflicts,
    handleConflictResolve,
  ]);

  // ============================================================
  // RENDER - Step-based
  // ============================================================
  if (state.step === "saving") {
    return (
      <SavingStep
        message="Lagrer endringer..."
        className={className}
        data-testid={testId}
        loadingTestId="edit-form-saving"
      />
    );
  }

  if (state.step === "conflict") {
    return (
      <ConflictStep
        conflictResolution={conflictResolution}
        onCancel={handleConflictCancel}
        onResolve={handleConflictResolve}
        className={className}
        data-testid={testId}
      />
    );
  }

  if (state.step === "selectSource") {
    const items =
      effectiveBookingType === "hard"
        ? projectsInSelection
        : customersInSelection;
    const selectedId =
      effectiveBookingType === "hard"
        ? (state.sourceProject?.id ?? null)
        : (state.sourceCustomer?.id ?? null);
    return (
      <SourceSelectStep
        bookingType={effectiveBookingType}
        items={items}
        selectedId={selectedId}
        onSelectSource={handleSelectSource}
        onContinue={handleContinueFromSelect}
        className={className}
        data-testid={testId}
      />
    );
  }

  // ============================================================
  // RENDER - Edit Step
  // ============================================================
  const showProjectSelector =
    effectiveBookingType === "hard" && !state.isAbsence;
  const showCustomerSelector =
    effectiveBookingType === "soft" && !isTargetCustomer;
  const showAbsenceCheckbox =
    effectiveBookingType === "hard" && !absenceDisabled;
  const showFullHoursInput =
    effectiveBookingType === "hard" ||
    (effectiveBookingType === "soft" && !isTargetCustomer) ||
    state.isAbsence;

  return (
    <>
      <FormContainer
        className={className}
        data-testid={testId}
        contentTestId="edit-form-edit"
      >
        <FormHeader>
          Redigerer {sourceBookings.length} booking
          {sourceBookings.length !== 1 ? "er" : ""} på «{sourceLabel}»
        </FormHeader>

        {showProjectSelector && (
          <ProjectSelector
            label="Nytt prosjekt"
            options={projectOptions}
            value={selectedProjectOption}
            onChange={handleProjectChange}
            loading={projectsLoading}
            knownCustomers={activeCustomers}
            testId="edit-form-project"
          />
        )}

        {/* Helper message when converting absence to hard booking and no project is selected */}
        {showProjectSelector && !state.targetProject && (
          <p
            className={formStyles.helperText}
            data-testid="edit-form-project-required"
          >
            Velg et prosjekt for å lagre bookingen
          </p>
        )}

        {showCustomerSelector && (
          <CustomerSelector
            label="Ny kunde"
            options={customerOptions}
            value={selectedCustomerOption}
            onChange={handleCustomerChange}
            loading={customersLoading}
            testId="edit-form-customer"
          />
        )}

        {showAbsenceCheckbox && (
          <AbsenceCheckbox
            checked={state.isAbsence}
            onChange={handleAbsenceToggle}
            testIdPrefix="edit-form"
          />
        )}

        <HoursInput
          variant={showFullHoursInput ? "full" : "simple"}
          value={state.hours}
          maxHours={maxHours}
          defaultHours={defaultHours}
          allowOvertime={state.allowOvertime}
          onChange={handleHoursChange}
          onInputChange={handleHoursInputChange}
          onOvertimeToggle={state.isAbsence ? undefined : handleOvertimeToggle}
          testIdPrefix="edit-form"
        />

        <FormActions
          submitLabel="Lagre endringer"
          submitIcon="save"
          onSubmit={handleSubmit}
          submitDisabled={!hasValidTarget || state.hours <= 0}
          backLabel={showBackButton ? "Tilbake" : undefined}
          onBack={showBackButton ? handleBackToSelect : undefined}
          testIdPrefix="edit-form"
        />
      </FormContainer>

      <Modal
        isOpen={showWeekendHolidayDialog}
        title={warningDialogTitle}
        onClose={handleDialogCancel}
        size="lg"
        data-testid="edit-form-weekend-holiday-dialog"
      >
        <div style={{ padding: "1rem" }}>
          <p style={{ marginBottom: "1.5rem" }}>
            Du redigerer {warningRedDayText}. Vil du fortsette med å redigere
            disse, eller hoppe over dem som ikke skal inkluderes?
          </p>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <ActionButton
              label="Avbryt"
              variant="danger"
              icon={<Icon name="x" size="sm" aria-hidden />}
              iconPosition="left"
              onClick={handleDialogCancel}
              data-testid="edit-form-weekend-dialog-cancel"
            />
            {canSkipRedDays && (
              <ActionButton
                label={skipButtonLabel}
                variant="secondary"
                icon={<Icon name="skip-forward" size="sm" aria-hidden />}
                iconPosition="left"
                onClick={handleSkipRedDays}
                data-testid="edit-form-weekend-dialog-skip"
              />
            )}
            <ActionButton
              label="Rediger uansett"
              variant="primary"
              icon={<Icon name="check" size="sm" aria-hidden />}
              iconPosition="left"
              onClick={handleEditAnyway}
              data-testid="edit-form-weekend-dialog-confirm"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

EditForm.displayName = "EditForm";

export default EditForm;
